import { mandatory, nonMandatory } from './storeFromRequestParamsValidation';
import { Article } from './template/template.state.types';

export const createPathWithExtension = (path: string | undefined, extension: string | undefined) => {
  if (!path || !extension) {
    return null;
  }
  return `${path}.${extension}`;
};

const createImageCroppingData = (croppingData: any) => ({
  x: mandatory(croppingData.x, 0),
  y: mandatory(croppingData.y, 0),
  width: mandatory(croppingData.width, 0),
  height: mandatory(croppingData.height, 0),
});

export const createImage = (host: string, aspectRatio?: object) => (image: any) => {
  const path = mandatory(image.path, '');
  const fileExtension = mandatory(image.fileExtension, '');
  return {
    host: mandatory(host, ''),
    path: createPathWithExtension(path, fileExtension),
    credit: nonMandatory(image.credit),
    caption: nonMandatory(image.caption),
    alt: nonMandatory(image.alt),
    link: nonMandatory(image.linkURL, (linkURL: string) => ({
      linkURL,
      targetAttributeValue: nonMandatory(image.linkTargetAttribute),
      relAttributeValue: nonMandatory(image.linkRelAttribute),
    })),
    cropping: nonMandatory(image.cropping, createImageCroppingData),
    ...aspectRatio ? { aspectRatio } : {},
  };
};

export const createCoverVideo = (media: any) => {
  const type = nonMandatory(media.type);
  if (type === 'youtube') {
    return {
      html: nonMandatory(media.value.html),
      mediaId: nonMandatory(media.value.mediaId),
      type,
    };
  }
  if (type === 'mmPlayer') {
    return {
      html: mandatory(media.value.html, ''),
      mediaId: mandatory(media.value.mediaId, ''),
      playerId: mandatory(media.value.playerId, ''),
      type,
    };
  }
  if (type === 'jw') {
    return {
      html: mandatory(media.value.html, ''),
      videoId: mandatory(media.value.videoId, ''),
      playerId: mandatory(media.value.playerId, ''),
      type,
    };
  }
  return null;
};

export const createAuthorPagePath = (editionEndpoint: string) => (path: string) => {
  return `https://${editionEndpoint}${path}`;
};

export const createAuthor = (imagesCDNHost: string, editionEndpoint: string) => (author: any) => {
  const defaultName = mandatory(author.name, '');
  const defaultUserName = mandatory(author.username, '');
  return {
    name: nonMandatory(author.authorInfo.name.value) || defaultName,
    byline: nonMandatory(author.authorInfo.byline.value),
    link: nonMandatory(author.pagePath, createAuthorPagePath(editionEndpoint)),
    image: nonMandatory(author.authorInfo.image.value, createImage(imagesCDNHost)),
    username: nonMandatory(author.userName) || defaultUserName,
  };
};

export const createAuthors = (hideOwner: boolean, imagesCDNHost: string, editionEndpoint: string) => (authors: any) => {
  const additionalAuthors = nonMandatory(authors.additional, (additional: any) => additional.map(createAuthor(imagesCDNHost, editionEndpoint)));
  const owner = mandatory(authors.owner, null, createAuthor(imagesCDNHost, editionEndpoint));
  const allAuthors = [
    ...((hideOwner || !owner) ? [] : [owner]),
    ...(additionalAuthors || []),
  ];
  return allAuthors;
};

const removeSeconds = (time: string) => time.replace(/:\d+\s/gi, ' ');

const DATE_MED = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
};
export const createDate = (locale: string, timeZone: string, articleDateISO: string) => {
  const articleDate = Date.parse(articleDateISO);
  return Intl.DateTimeFormat(locale, { timeZone, ...DATE_MED }).format(articleDate);
};

const TIME_WITH_SHORT_OFFSET = {
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
  timeZoneName: 'short',
};
export const createTime = (locale: string, timeZone: string, articleDateISO: string) => {
  const articleDate = Date.parse(articleDateISO);
  return removeSeconds(Intl.DateTimeFormat(locale, { timeZone, ...TIME_WITH_SHORT_OFFSET }).format(articleDate));
};

export const createTimeAndDate = (locale: string, timeZone: string, articleDateISO: string) => {
  const articleDate = Date.parse(articleDateISO);
  return removeSeconds(Intl.DateTimeFormat(locale, { timeZone, ...TIME_WITH_SHORT_OFFSET, ...DATE_MED }).format(articleDate));
};


export const createShortDateOrTime = (midnightInTimeZoneISO: string, timeZone: string, locale: string) => (articleDateISO: string) => {
  const articleDate = Date.parse(articleDateISO);
  const midnightInTimeZone = Date.parse(midnightInTimeZoneISO);
  const time = createTime(locale, timeZone, articleDateISO);
  const date = createDate(locale, timeZone, articleDateISO);
  return articleDate >= midnightInTimeZone ? time : date;
};

export const createArticles = (imagesHost: any, midnightInTimeZoneISO: string, timeZone: string, locale: string, editionEndpoint: string) => (articles: Array<any>) => {
  return articles.map((article: any): Article => {
    const hideOwner = nonMandatory(article.metadata.hideOwner);
    return {
      id: mandatory(article.id, ''),
      title: mandatory(article.title, ''),
      image: mandatory(article.cover.image.value, {}, createImage(imagesHost)),
      coverType: mandatory(article.coverType, ''),
      articleUrl: mandatory(article.articleUrl, ''),
      createdAt: mandatory(article.createdAt, '', createShortDateOrTime(midnightInTimeZoneISO, timeZone, locale)),
      createdAtISO: mandatory(article.createdAt, ''),
      updatedAt: mandatory(article.updatedAt, '', createShortDateOrTime(midnightInTimeZoneISO, timeZone, locale)),
      updatedAtISO: mandatory(article.updatedAt, ''),
      featuredAt: mandatory(article.featuredAt, ''),
      authors: mandatory(article.authors, [], createAuthors(hideOwner, imagesHost, editionEndpoint)),
      description: mandatory(article.seoDescription, ''),
      mainCategory: nonMandatory(article.mainCategory.displayName),
      sponsoredText: nonMandatory(article.metadata.sponsor.text),
      brandName: nonMandatory(article.metadata.sponsor.brandName),
      logo: nonMandatory(article.metadata.sponsor.image),
    };
  });
};
